import * as Sentry from '@sentry/react';
import { format, getQuarter, getYear } from 'date-fns';
import { KpiPeriod } from '../data-models/company-financials.data-model';
import {
  KPIRequestFrequency,
  KPIRequestStatus,
  kpiRequestStatusToConfigMap,
} from '../data-models/kpi-requests.data-model';
import { MONTHS } from '../pages/KPI/components/Sections/FormItem/KPITable/useKPITableMetricsInputs';
import { BaseLocationDataModel } from '../schemas/Location.schema';
import { formatEntityCurrency } from './formatters/EntityManagementFormatters';
import { formatTransactionSource } from './formatters/TransactionSourceFormatters';
import { FormatterFn } from './formatter-service';
import { camelCaseToCapitalizedString } from './stringUtils';

export const StaticFormattersById = {
  kpiStatus: formatKpiRequestStatus as FormatterFn<unknown>,
  kpiPeriod: formatKPIPeriod as FormatterFn<unknown>,
  fye: formatCompanyFye as FormatterFn<unknown>,
  location: formatLocation as FormatterFn<unknown>,
  locationShort: formatLocationShort as FormatterFn<unknown>,
  fileSize: formatFileSize as FormatterFn<unknown>,
  period: formatDocumentPeriod as FormatterFn<unknown>,
  currencyCode: formatCurrencyCode as FormatterFn<unknown>,
  entityCurrency: formatEntityCurrency as FormatterFn<unknown>,
  noteConversionStatus: formatNoteConversionStatus as FormatterFn<unknown>,
  roundAssignmentStatus: formatRoundAssignmentStatus as FormatterFn<unknown>,
  transactionSource: formatTransactionSource as FormatterFn<unknown>,
} satisfies Record<string, FormatterFn<unknown>>;

const NOTE_CONVERSION_STATUS_LABELS: Record<string, string> = {
  converted: 'Converted',
  live: 'Live',
  unknown: 'Unknown',
};

/**
 * A SAFE or note's conversion status. The notes form offers only Live and Converted, so a select formatter
 * built from its options would leave a stored unknown raw on whichever screen formatted it first.
 */
function formatNoteConversionStatus(value: unknown): string {
  if (typeof value !== 'string') {
    return '';
  }
  return NOTE_CONVERSION_STATUS_LABELS[value] ?? value;
}

const ROUND_ASSIGNMENT_STATUS_LABELS: Record<string, string> = {
  assigned: 'Assigned',
  pendingAssignment: 'Pending Assignment',
  unassigned: 'Unassigned',
};

/**
 * The round planner's verdict on a transaction, derived by the API and null on a hand-entered row.
 * A grid keys its filter list and its group rows on the label and then formats that key a second
 * time to display it, so a label this map has already produced passes straight back through.
 */
function formatRoundAssignmentStatus(value: unknown): string {
  if (typeof value !== 'string') {
    return '';
  }
  return ROUND_ASSIGNMENT_STATUS_LABELS[value] ?? value;
}

const ISO_DATE_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * Document Hub reporting period. Starbreaker resolves the classifier's period text to a
 * period-end date, which reaches the UI as an ISO date or datetime string; it reads as a
 * numeric date in the user's locale (MM/DD/YYYY in en-US, DD/MM/YYYY elsewhere), the same
 * form the calendar control edits it in. A value that is not a date (a legacy free-text
 * period) is shown as is.
 */
function formatDocumentPeriod(value: unknown): string {
  if (typeof value !== 'string' || value.trim() === '') {
    return '';
  }
  const match = ISO_DATE_PREFIX.exec(value);
  if (!match) {
    return value;
  }
  const [year, month, day] = match.slice(1).map(Number);
  const locale = typeof navigator !== 'undefined' ? navigator.language : undefined;
  return new Intl.DateTimeFormat(locale, {
    timeZone: 'UTC',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(Date.UTC(year, month - 1, day)));
}

/** ISO currency code display form — uppercased, blank for non-string input. */
function formatCurrencyCode(value: unknown): string {
  return typeof value === 'string' ? value.trim().toUpperCase() : '';
}

/**
 * Human-readable file size from a byte count (e.g. 512230 → "512.23 KB",
 * 13_369_344_000 → "12.45 GB"). Unlike the older `convertFileSize` helper this
 * covers GB/TB, which the Document Hub needs.
 */
function formatFileSize(value: unknown): string {
  const bytes = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(bytes) || bytes < 0) {
    return '';
  }
  if (bytes < 1024) {
    return `${Math.round(bytes)} B`;
  }
  const units = ['KB', 'MB', 'GB', 'TB', 'PB'];
  let size = bytes / 1024;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(2)} ${units[unitIndex]}`;
}

export interface IKPIFormatterConfig {
  frequency: KpiPeriod | KPIRequestFrequency;
  showYear?: boolean;
  yearFormat?: string;
}

function formatKPIPeriod(date: string | Date, config: IKPIFormatterConfig) {
  const { frequency, showYear = false } = config ?? {};
  const validationResult = validateDateInput(date);
  if (!validationResult.valid) {
    return validationResult.reason === 'null' ? '' : 'Invalid Date';
  }

  const dateValue = validationResult.date;

  if (frequency === KpiPeriod.year || frequency === KPIRequestFrequency.Annual) {
    return getYear(dateValue).toString();
  } else if (frequency === KpiPeriod.quarter || frequency === KPIRequestFrequency.Quarterly) {
    return `Q${getQuarter(dateValue)} ${showYear ? getYear(dateValue) : ''}`;
  } else {
    const yearFormat = config.yearFormat ?? 'yy';
    return format(dateValue, `MMM${showYear ? ` ${yearFormat}` : ''}`);
  }
}

function formatCompanyFye(fye: number) {
  if (fye < 1 || fye > 12) {
    Sentry.logger.warn('Invalid fye value', { fye });
    return '';
  }
  return MONTHS[fye - 1];
}

function formatKpiRequestStatus(status: KPIRequestStatus) {
  return kpiRequestStatusToConfigMap[status]?.label ?? camelCaseToCapitalizedString(status);
}

function formatLocation(data: BaseLocationDataModel | BaseLocationDataModel[] | null | undefined): string {
  if (Array.isArray(data)) {
    return data.map(_formatLocation).join('; ');
  }
  return _formatLocation(data);
}

/**
 * City and state only — "Austin, TX".
 *
 * The `location` formatter appends the country ("Austin, TX, USA"), which is more
 * than the structure chart's operating descriptor has room for.
 */
function formatLocationShort(data?: BaseLocationDataModel | null): string {
  if (!data) {
    return '';
  }
  const { city = '', state = '', stateCode = '' } = data;

  return [city, stateCode || state].filter((part) => part !== '' && part).join(', ');
}

function _formatLocation(data?: BaseLocationDataModel | null) {
  if (!data) {
    return '';
  }
  const { city = '', state = '', country = '', countryCode = '', stateCode = '' } = data;

  if (country === 'United States' || country === 'USA') {
    return [city, stateCode || state, countryCode || country].filter((str) => str !== '' && str).join(', ');
  }

  return [city, country].filter((str) => str !== '' && str).join(', ');
}

type DateValidationResult = { valid: true; date: Date } | { valid: false; reason: 'null' | 'invalid' };
export function validateDateInput(date: string | Date | null | undefined): DateValidationResult {
  if (date == null) {
    return { valid: false, reason: 'null' };
  }

  const dateValue = date instanceof Date ? date : new Date(date);

  if (isNaN(dateValue.getTime())) {
    return { valid: false, reason: 'invalid' };
  }

  return { valid: true, date: dateValue };
}
